import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IBrand, IBrandResponse } from '@boma/shared/entities';
import {
  DefinitionTarget,
  ICategory,
  IDefinition,
  IDepartmentResponse,
} from '@boma/supplier-portal/shared/entities';
import {
  BrandsService,
  DefinitionsService,
  SearchService,
} from '@boma/supplier-portal/shared/services';
import { getSelectedSupplierId } from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';
import { MenuItem, MessageService, TreeNode } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'boma-definition-creator',
  templateUrl: './boma-definition-creator.component.html',
  styleUrls: ['./boma-definition-creator.component.scss'],
})
export class BomaDefinitionCreatorComponent implements OnInit {
  @Input() selectedTarget: DefinitionTarget = 4; //   Customer = 1, Product = 2, Location = 4,
  @Input() productOwner = false;
  @Input() selectedDepartment: any = {
    name: '',
    id: '',
  };
  @Input() selectedCategory: any = {
    name: '',
    id: '',
  };

  @Input() selectedBrand: any = {
    name: '',
    id: '',
  };

  @Input() cols: any[] = [
    { field: 'value', header: 'Title' },
    { field: 'type', header: 'Type' },
    { field: 'isSelectable', header: 'Selectable' },
  ];
  @Input() contextName = 'Item';
  @Input() showHeader = false;
  @Input() showLabels = true;
  @Input() layout = 'horizontal';
  @Input() targetId: string; // the id of the location, product or customer we are attaching definitions to.
  @Input() singleSelection = true;
  @Input() editing = true;

  @Output() selectedItem: EventEmitter<any> = new EventEmitter();
  @Output() selectDepartments: EventEmitter<any> = new EventEmitter();
  @Output() selectCategories: EventEmitter<any> = new EventEmitter();
  @Output() selectBrands: EventEmitter<any> = new EventEmitter();
  @Output() saveProduct: EventEmitter<any> = new EventEmitter();

  selectedTargetName = DefinitionTarget[this.selectedTarget];

  entityId$: Observable<string>;
  entityId: string;
  entityIdSubscription: Subscription;

  loading: boolean;
  showAdvancedCreationOptions = false; // Hiding this on the dialogue atm, when we break out into complicated trees this will become relevant
  complexDefinitions = false;
  maximumAllowedParents = 20;
  maximumAllowedChildren = 100;
  displayEditDialogue = false;
  definitionsBeingCreated: string[] = [];

  selectedCategories: ICategory[] = [];
  selectedBrands: IBrand[] = [];

  reservedNames = [
    'departments',
    'department',
    'brands',
    'brand',
    'categories',
    'category',
  ];

  currentDepartments: IDefinition;
  departmentOptions: IDepartmentResponse[] = [];
  selectedDepartmentOptions: IDepartmentResponse[] = [];
  currentDefinitions: { [key: string]: IDefinition[] } = {};

  //might have to update the types on these categories
  currentCategories: IDefinition; //overlap with selected categories?
  categoryOptions: ICategory[] = [];
  selectedCategoryOptions: ICategory[] = [];

  currentBrands: IDefinition;
  brandOptions: IBrandResponse[] = [];
  selectedBrandsOptions: IBrandResponse[] = [];

  brands: any = [];
  createFormGroup: FormGroup = new FormGroup({
    value: new FormControl('', Validators.required),
    isSelectable: new FormControl(true, Validators.required),
  });
  definitions: TreeNode[];
  definitionIdTree: { label: string; id: string }[] = [
    { label: 'All', id: '' },
  ];
  // Set this to true when in staging environment (use environment variable)
  isDeveloperMode = false;
  items: MenuItem[];
  originalResponse: any[];
  loaded = false;
  awaitingResponse = false;
  displayDialogue = false;
  definitionTypeOptions: { label: string; value: string }[];
  selectedNode: TreeNode;
  selectedDefinition: IDefinition;
  linkedDefinitions: IDefinition[] = [];
  departmentsLoaded = false;
  categoriesLoaded = false;
  brandsLoaded = false;
  creatingPrimary = false;

  // TODO On close of the multiselected filter the selected options to the top, for easier finding?
  constructor(
    private definitionsService: DefinitionsService,
    private store: Store,
    private searchService: SearchService,
    private messageService: MessageService,
    private brandService: BrandsService
  ) {}

  ngOnInit(): void {
    this.selectedTargetName = DefinitionTarget[this.selectedTarget];

    this.definitionTypeOptions = Object.keys(DefinitionTarget)
      .filter((e) => isNaN(Number(e)))
      .map((key) => ({
        label: key,
        value: DefinitionTarget[key],
      }));

    this.items = [
      {
        label: 'Location Groupings',
        id: '4',
        command: (event) => {
          this.getDefinitions(4, 'Location');
        },
      },
      {
        label: 'Product Groupings',
        id: '2',
        command: (event) => {
          this.getDefinitions(2, 'Product');
        },
      },
      {
        label: 'Customer Groupings',
        id: '1',
        command: (event) => {
          this.getDefinitions(1, 'Customer');
        },
      },
    ];

    this.entityId$ = this.store.select(getSelectedSupplierId);
    this.entityIdSubscription = this.entityId$.subscribe(
      (id) => (this.entityId = id)
    );

    this.definitionsService
      .getBrands(this.entityId)
      .subscribe((x) => (this.brands = x));

    this.searchService.getDepartments('').subscribe((result) => {
      this.departmentOptions = result;
      this.getDefinitions();
    });
    //We never get category options I guess because we don't technically have a category call for all

    //When we create we show only the suppliers brands
    this.brandService
      .listBrands(this.entityId, '', '1', '500')
      .subscribe((result) => {
        this.brandOptions = result.items;
        this.getDefinitions();
      });
  }

  getDefinitions(id = null, name = null) {
    if (id && name) {
      this.selectedTarget = id;
      this.selectedTargetName = name;
    }
    //Get the definitions we can choose from
    this.selectedDefinition = null;
    this.definitionIdTree.length = 1;
    this.definitionsService
      .getDefinitions(
        this.entityId, // entity id
        this.selectedTarget, // definition target
        this.selectedDefinition?.id || ''
      )
      .subscribe((payload) => {
        this.mapTreeNodes(payload);
        this.loaded = true;
      });
  }

  compareString(stringOne, stringTwo) {
    const a = stringOne.toLowerCase();
    const b = stringTwo.toLowerCase();
    return a === b;
  }

  createPrimaryDefinition(
    categoryName,
    selectedTarget = DefinitionTarget.Product
  ) {
    if (this.creatingPrimary) return;
    this.creatingPrimary = true;
    this.createFormGroup.patchValue({
      isSelectable: false,
      value: categoryName,
    });

    this.selectedTarget = selectedTarget;
    // this.selectedTarget = DefinitionTarget.Product;
    this.createDefinition();
  }

  // Check that the default definitions (Department) exist
  // If it does not exist we need to create it
  checkForDefaultDefinitions(ev) {
    if (!ev?.length) return;

    const definitions = ev;
    this.selectedDepartmentOptions = [];

    const defaultDefinitions = ['department', 'categories', 'brands'];
    const definitionNames = definitions.map((x) => x.value.toLowerCase());

    defaultDefinitions.forEach((defaultName) => {
      const target = defaultName[0].toUpperCase() + defaultName.substring(1);
      const exists = definitionNames.includes(defaultName);

      if (exists) {
        const matchingDef = definitions.find((d) =>
          this.compareString(d.value, defaultName)
        );
        if (defaultName === 'categories')
          this.populateDefinitionCategories(matchingDef);
        if (defaultName === 'brands') {
          this.currentBrands = matchingDef;
          this.populateDefinitionBrands();
        }
        if (defaultName === 'department')
          this.populateDefinitionDepartment(matchingDef);
      } else {
        this.createPrimaryDefinition(target);
      }
    });
  }

  populateDefinitionDepartment(definition) {
    //clean these up remove any empty departments that might exist...
    definition.children = definition.children.filter(
      (x) => x.value && x.isSelectable
    );
    // we have the departments so we can check against what is ticked
    // take the department names and match them with the actual departments
    this.currentDepartments = definition;

    this.departmentOptions.forEach((d) => {
      //push the id of the department to the definition linkId
      this.currentDepartments.children.map((x) => {
        // basically the department array
        if (x.value === d.name) {
          if (x.isSelectable) {
            this.selectedDepartmentOptions.push(d);
          }
          x.linkId = d.id;
          return x;
        }
      });
    });
    this.departmentsLoaded = true;
  }

  populateDefinitionBrands() {
    this.currentBrands.children = this.filterSelectableResults(
      this.currentBrands
    );
    this.brandOptions.forEach((b) => {
      this.currentBrands.children.map((x) => {
        if (x.value === b.name) {
          if (x.isSelectable) {
            this.selectedBrandsOptions.push(b);
          }
          x.linkId = b.id;
          return x;
        }
      });
    });
    this.brandsLoaded = true;
  }

  populateDefinitionCategories(definition) {
    definition.children = definition.children.filter(
      (x) => x.value && x.isSelectable
    );
    this.currentCategories = definition;
    this.categoryOptions.forEach((c) => {
      this.currentCategories.children.map((x) => {
        if (x.value === c.categoryLocaleMetadata[0].name) {
          if (x.isSelectable) {
            this.selectedCategoryOptions.push(c);
          }
          x.linkId = c.categoryLocaleMetadata[0].id;
          return x;
        }
      });
    });
    this.categoriesLoaded = true;
  }

  mapTreeNodes(payload) {
    if (this.selectedTarget === DefinitionTarget.Product) {
      this.checkForDefaultDefinitions(payload);
    }

    this.originalResponse = payload;

    // Filter unique definitions by value (case-insensitive)
    const uniqueDefinitions = [];
    const seenValues = new Set<string>();
    for (const def of payload) {
      const valueLower = def.value.toLowerCase();
      if (!seenValues.has(valueLower)) {
        seenValues.add(valueLower);
        uniqueDefinitions.push(def);
      }
    }

    if (payload.length !== uniqueDefinitions.length) {
      console.warn('Duplicates detected in definitions:', payload);
      // this.messageService.add({
      //   severity: 'warn',
      //   summary: 'Duplicate Definitions',
      //   detail: 'Duplicate entries were removed from the display.',
      // });
    }

    const reservedOrder = ['department', 'categories', 'brands'];
    const reservedDefinitions = [];
    const otherDefinitions = [];

    uniqueDefinitions.forEach((def) => {
      const valueLower = def.value.toLowerCase();
      if (reservedOrder.includes(valueLower)) {
        reservedDefinitions.push(def);
      } else {
        otherDefinitions.push(def);
      }
    });

    reservedDefinitions.sort((a, b) => {
      return (
        reservedOrder.indexOf(a.value.toLowerCase()) -
        reservedOrder.indexOf(b.value.toLowerCase())
      );
    });

    const sortedDefinitions = [...reservedDefinitions, ...otherDefinitions];

    this.definitions = sortedDefinitions.map((x) => {
      if (!this.currentDefinitions[x.id]) {
        this.currentDefinitions[x.id] = [];
      }
      return {
        data: x,
        children: x.children.map((y) => ({
          data: { ...y },
        })),
      } as TreeNode;
    });
  }

  filterSelectableResults(data) {
    return data.children.filter((x) => x.value && x.isSelectable);
  }

  editDefinition(rowData) {
    this.displayEditDialogue = true;
    this.selectedDefinition = rowData;
  }

  updateDefinitionTitle(id, index, isSelectable = false) {
    const title = <HTMLInputElement>(
      document.getElementById('definition-' + index)
    );

    if (!title.classList.contains('p-filled')) {
      this.messageService.add({
        severity: 'info',
        summary: 'Title Required',
        detail: 'You cannot use a blank name for a definition',
      });
      return;
    }

    // TODO a backend check too, undefined scope of functionality, so lives in front end until approved
    if (this.checkReservedName(title.value)) return;

    this.updateDefinition(id, this.entityId, title.value.trim(), isSelectable);
  }

  updateDefinition(id, entityId, title, selectable) {
    this.definitionsService
      .updateDefinitionTitle(id, entityId, title.trim(), selectable)
      .subscribe((x) => {
        this.originalResponse = this.originalResponse.map((d) =>
          d.id === x.id ? x : d
        );

        this.mapTreeNodes(this.originalResponse);
      });
  }

  checkReservedName(title) {
    if (this.reservedNames.indexOf(title.trim().toLowerCase()) === -1)
      return false;
    this.messageService.add({
      severity: 'warn',
      summary: 'Reserved Name',
      detail:
        title +
        ' is a reserved definition name that cannot be changed, please select a different name',
    });
    return true;
  }

  addDefinition(rowData) {
    console.log(rowData);
    if (!rowData) {
      //Limit to 5 main definitions
      if (this.definitions.length >= this.maximumAllowedParents) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Limit Reached',
          detail:
            'You are limited to a maximum of 5 definitions, please contact support&#64;boma.today to find out how to obtain more.',
        });
        return;
      }
      //dont let them select it as it's a top level definition
      this.createFormGroup.patchValue({
        isSelectable: false,
      });
    } else {
      //default to selectable if not top level
      this.createFormGroup.patchValue({
        isSelectable: true,
      });
    }
    this.displayDialogue = true;
    this.selectedDefinition = rowData || null;
  }

  linkDefinition(rowData) {
    const definitionIndex = this.linkedDefinitions.findIndex(
      (x) => x.id === rowData.id
    );

    if (definitionIndex === -1) {
      this.linkedDefinitions.push(rowData);
    } else {
      this.linkedDefinitions.splice(definitionIndex, 1);
    }

    this.selectedItem.emit({
      definitions: this.linkedDefinitions,
      target: this.selectedTargetName,
    });
  }

  // TODO tree node seems to involved for the requirement might be able to remove it going forward (its not being used atm) would also simplify the mapping
  createDefinition(linkId = null) {
    if (!this.createFormGroup.get('value').value) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Name Required',
        detail: 'A definition grouping requires a name',
      });

      return;
    }
    const definitionName = this.createFormGroup.get('value').value;
    //Check that this not being created
    if (
      this.definitionsBeingCreated.indexOf(definitionName.toLowerCase()) > -1
    ) {
      return;
    }

    this.definitionsBeingCreated.push(definitionName.toLowerCase());
    //show a loader of some kind
    this.awaitingResponse = true;
    this.definitionsService
      .createDefinition(
        this.selectedTargetName,
        this.createFormGroup.get('value').value,
        this.createFormGroup.get('isSelectable').value,
        linkId,
        this.selectedTarget,
        this.selectedDefinition?.id,
        this.entityId,
        []
      )
      .subscribe((x) => {
        this.createFormGroup.reset();
        if (this.selectedDefinition) {
          this.originalResponse
            .find((y) => y.id === x.parentId)
            .children.push({
              id: x.id,
              type: x.type,
              value: x.value,
              isSelectable: x.isSelectable,
              targets: x.targets,
            });
        } else {
          this.originalResponse.push({
            id: x.id,
            type: x.type,
            value: x.value,
            isSelectable: x.isSelectable,
            targets: x.targets,
            children: [],
          });
        }

        if (this.creatingPrimary) {
          this.creatingPrimary = false;
        }
        this.mapTreeNodes(this.originalResponse);

        this.awaitingResponse = false;
        this.displayDialogue = false;
      });
  }

  nodeSelect(event) {
    if (!event.node.data.children) {
      this.loadChildDefinitions(event.node.data);
    }
  }

  //TODO THESE UPDATES ARE SUPERFICIAL AND CAN BE REMOVED LOGIC EXISTS IN DEFINITION SELECTOR COMPONENT
  updateDefinitionSelection(ev, parentId = null) {
    if (parentId) this.currentDefinitions[parentId] = ev;
    this.linkedDefinitions = [];
    Object.keys(this.currentDefinitions).forEach((x) => {
      this.currentDefinitions[x].map((y) => {
        this.linkedDefinitions.push(y);
      });
    });

    this.selectedItem.emit(this.linkedDefinitions);
  }

  loadChildDefinitions(selectedNode, index = null) {
    this.definitionsService
      .getDefinitions(
        this.entityId,
        this.selectedTarget,
        selectedNode.id || selectedNode
      )
      .subscribe((x) => {
        if (selectedNode && !index) {
          this.definitionIdTree.push({
            label: selectedNode.value,
            id: selectedNode.id,
          });
        } else {
          // Shorten to the selected Node
          this.definitionIdTree.length = index + 1;
        }

        // TODO append to the tree rather than replace it
        this.mapTreeNodes(x);
      });
  }

  handleCategories(ev) {
    this.selectedCategories = ev;
    this.selectCategories.emit(this.selectedCategories);
  }

  handleDefinitionCategoryChange(ev) {
    this.selectedCategory.name = ev.value.value;
    this.selectedCategory.id = ev.value.linkId;

    this.selectCategories.emit([this.selectedCategory]);
  }

  handleDefinitionDepartmentChange(ev) {
    this.selectedDepartment.name = ev.value.value;
    this.selectedDepartment.id = ev.value.linkId;
    this.selectDepartments.emit(this.selectedDepartment);
  }

  // Implementing a function to enable suppliers to define which departments can be used by their employees when creating products.
  updateCategoryOptions(ev) {
    let exists = false;

    ev.forEach((x) => {
      this.currentCategories.children.map((d) => {
        if (x.name === d.value) {
          exists = true;
          d.isSelectable = !d.isSelectable;
          this.updateDefinition(d.id, this.entityId, d.value, d.isSelectable);
        }
      });

      if (!exists) {
        this.createFormGroup.patchValue({
          isSelectable: true,
          value: x.name,
        });
        this.selectedDefinition = this.currentCategories;
        this.createDefinition(x.id);
      }
    });
  }

  updateOptions(ev, currentOptions) {
    let exists = false;
    currentOptions.children.map((d) => {
      if (ev.itemValue.name === d.value) {
        exists = true;
        d.isSelectable = !d.isSelectable;
        this.updateDefinition(d.id, this.entityId, d.value, d.isSelectable);
      }
    });
    if (!exists) {
      this.createFormGroup.patchValue({
        isSelectable: true,
        value: ev.itemValue.name,
      });
      this.selectedDefinition = currentOptions;
      this.createDefinition(ev.itemValue.id);
    }

    //Update this now
    currentOptions.children = currentOptions.children.filter((x) => x.value);
  }

  // Moving attaching directly into this component for simplicity and less duplicated work, can be brought into the individual creates later on.
  linkDefinitionsToTarget() {
    if (!this.targetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'No Grouping Specified',
        detail: 'There was no specified grouping scope selected',
      });

      return;
    }

    if (
      this.selectedDepartment ||
      this.selectedCategory ||
      this.linkedDefinitions.length
    ) {
      if (this.linkedDefinitions.length) {
        const definitionsArray = this.linkedDefinitions.map((x) => x?.id);
        this.definitionsService
          .attachDefinitions(
            this.targetId, // target id
            this.entityId, // entity id
            this.selectedTarget, // definition target
            definitionsArray
          )
          .subscribe((payload) => {
            //need to emit success so we can tell the product edit to close.
          });
      }
      if (this.selectedDepartment || this.selectedCategory) {
        // dispatch product save... one time
        this.saveProduct.emit();
      }
    }
  }
}
